
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Trophy, Flame, Target, Star, ChevronRight, 
  Award, Zap, Users, Shield, TrendingUp,
  Lock, CheckCircle, Clock, Search, Filter, Calendar, AlertCircle
} from 'lucide-react';
import { calculateLevel, getXPForNextLevel } from './services/gamificationService';
import { User, Challenge, Badge, SupportedLanguage } from './types';
import { getLeaderboardEntries, LeaderboardEntry } from './services/leaderboardService';
import { getPublicProfilesByIds, PublicProfile } from './services/publicProfileService';
import { subscribeToFollowing } from './services/followService';
import i18n from './i18n/index';

// --- CALENDAR COUNTER (replaces streak) ---
export const CalendarCounter = ({ user }: { user: User }) => {
  const { t } = useTranslation();
  const [stampsThisMonth, setStampsThisMonth] = useState(0);
  
  useEffect(() => {
    // Get current month calendar stamps
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    
    const currentCalendar = (user.monthlyCalendars || []).find(
      cal => cal.year === year && cal.month === month && !cal.archived
    );
    
    setStampsThisMonth(currentCalendar?.totalStamps || 0);
  }, [user.monthlyCalendars]);
  
  return (
    <div className="flex items-center gap-2 bg-emerald-500/10 text-emerald-400 px-4 py-2 rounded-2xl border border-emerald-500/20 shadow-lg shadow-emerald-500/5">
      <CheckCircle size={18} fill="currentColor" />
      <span className="font-black text-lg">{stampsThisMonth} {t('calendar.stamps_this_month_short')}</span>
    </div>
  );
};

// --- LEVEL PROGRESS BAR ---
export const LevelProgress = ({ xp, level, t }: { xp: number; level: number; t?: any }) => {
  const xpForNext = getXPForNextLevel(level);
  const xpForCurrent = level > 1 ? getXPForNextLevel(level - 1) : 0;
  const progress = ((xp - xpForCurrent) / (xpForNext - xpForCurrent)) * 100;

  const labelXP = typeof t === 'function'
    ? t('common.xp_points')
    : 'XP';

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-end">
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
          {labelXP}
        </span>
        <span className="text-xs font-black text-emerald-400">
          {Number(xp || 0).toLocaleString()} / {Number(xpForNext || 0).toLocaleString()} XP
        </span>
      </div>
      <div className="h-3 bg-slate-800 rounded-full overflow-hidden border border-slate-700/50 p-0.5">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-cyan-400 rounded-full transition-all duration-1000"
          style={{ width: `${Math.min(progress || 0, 100)}%` }}
        />
      </div>
    </div>
  );
};


// --- BADGE GRID ---
export const BadgeCollection = ({ unlockedBadges }: { unlockedBadges: string[] }) => {
  const { t } = useTranslation();
  const ALL_BADGES: Badge[] = [
    { id: 'first_steps', name: t('badges.first_steps_name'), description: t('badges.first_steps_desc'), icon: '👣', category: 'fitness' },
    { id: 'consistent', name: t('badges.consistent_name'), description: t('badges.consistent_desc'), icon: '🔥', category: 'streak' },
    { id: 'dedicated', name: t('badges.dedicated_name'), description: t('badges.dedicated_desc'), icon: '🏆', category: 'streak' },
    { id: 'social_butterfly', name: t('badges.social_butterfly_name'), description: t('badges.social_butterfly_desc'), icon: '🦋', category: 'social' },
    { id: 'clean_eater', name: t('badges.clean_eater_name'), description: t('badges.clean_eater_desc'), icon: '🍏', category: 'diet' },
    { id: 'early_bird', name: t('badges.early_bird_name'), description: t('badges.early_bird_desc'), icon: '🌅', category: 'fitness' },
  ];

  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
      {ALL_BADGES.map(badge => {
        const isUnlocked = unlockedBadges.includes(badge.id);
        return (
          <div key={badge.id} className={`group relative aspect-square rounded-2xl flex flex-col items-center justify-center border transition-all ${
            isUnlocked 
              ? 'bg-slate-900 border-emerald-500/30 text-emerald-400 shadow-lg' 
              : 'bg-slate-900/30 border-slate-800 text-slate-600 grayscale'
          }`}>
            <span className="text-3xl mb-1 group-hover:scale-125 transition-transform">{badge.icon}</span>
            <span className="text-[8px] font-black uppercase text-center px-1">{badge.name}</span>
            {!isUnlocked && <Lock size={10} className="absolute top-2 right-2 opacity-30" />}
            
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-0 group-hover:w-full group-hover:h-auto overflow-hidden bg-slate-800 text-[8px] p-2 rounded-lg z-10 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all pointer-events-none border border-slate-700">
              {badge.description}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- CHALLENGE CARD ---
// Added key property to props type to satisfy TypeScript JSX assignment in lists
export const ChallengeCard = ({ challenge, t, onStartChallenge }: { challenge: Challenge, key?: React.Key, t?: any, onStartChallenge?: (challengeId: string) => void }) => {
  const handleClick = () => {
    if (onStartChallenge) {
      onStartChallenge(challenge.id);
    } else {
      // Fallback: show message that challenge tracking is coming soon
      alert(t('dashboard.challenges_coming_soon') || t('common.coming_soon'));
    }
  };

  return (
  <div className="bg-slate-900 border border-slate-800 p-5 rounded-[2rem] hover:border-emerald-500/30 transition-all group relative overflow-hidden">
    <div className="flex justify-between items-start mb-4">
      <div className={`p-2 rounded-xl ${
        challenge.type === 'fitness' ? 'bg-blue-500/10 text-blue-500' :
        challenge.type === 'diet' ? 'bg-emerald-500/10 text-emerald-500' :
        'bg-purple-500/10 text-purple-500'
      }`}>
        {challenge.type === 'fitness' ? <Zap size={20}/> : challenge.type === 'diet' ? <Target size={20}/> : <Users size={20}/>}
      </div>
      <div className="flex items-center gap-1 bg-slate-800 px-3 py-1 rounded-full">
        <Star size={12} className="text-yellow-500" fill="currentColor"/>
        <span className="text-[10px] font-black text-white">+{challenge.xpReward} XP</span>
      </div>
    </div>

    <h3 className="font-bold text-lg mb-1">{challenge.title}</h3>
    <p className="text-xs text-slate-500 leading-relaxed mb-6">{challenge.description}</p>

    <div className="space-y-3">
      <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider">
          <span className="text-slate-500">{t('goals.progress')}</span>
        <span className="text-emerald-400">{challenge.currentValue || 0}%</span>
      </div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${challenge.currentValue || 0}%` }} />
      </div>
    </div>

      <button 
        onClick={handleClick}
        className="w-full mt-6 py-3 bg-slate-800 hover:bg-emerald-500 text-white rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2"
      >
        {challenge.status === 'active' ? t('goals.continue') : t('goals.start_challenge')}
      <ChevronRight size={14}/>
    </button>
  </div>
);
};

// --- LEADERBOARD (PRODUCTION SAFE, FULL FEATURES) ---
export const Leaderboard = ({ user, authReady, t }: { user: User; authReady: boolean; t?: any }) => {
  const [activeTab, setActiveTab] = React.useState<'global' | 'friends'>('global');
  const [leaders, setLeaders] = React.useState<User[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<'ACCESS_DENIED' | 'UNKNOWN_ERROR' | null>(null);

  // Helper: Convert LeaderboardEntry to User for UI compatibility
  const entryToUser = (entry: LeaderboardEntry): User => {
    return {
      id: entry.userId,
      userId: entry.userId,
      name: entry.name,
      avatar: entry.avatar,
      level: entry.level,
      xp: entry.xp,
      title: entry.title,
      email: '', // Not available in leaderboard entry
      badges: [], // Not available in leaderboard entry
      public: true, // Leaderboard entries are always public
      role: 'user',
      plan: 'free',
      activeChallenges: [],
    } as User;
  };

  // Helper: Convert PublicProfile to User for UI compatibility
  const profileToUser = (profile: PublicProfile): User => {
    return {
      id: profile.userId,
      userId: profile.userId,
      name: profile.name,
      avatar: profile.avatar,
      level: profile.level,
      xp: profile.xp,
      title: profile.title,
      email: '', // Not available in public profile
      badges: profile.badges || [],
      public: profile.isPublic,
      role: 'user',
      plan: 'free',
      activeChallenges: [],
    } as User;
  };

  // Global tab: Load leaderboard (one-shot)
  React.useEffect(() => {
    if (activeTab !== 'global') return;

    setLoading(true);
    setError(null);

    // 🔒 CRITICAL FIX: Use one-shot getDocs() instead of onSnapshot for leaderboard
    const loadLeaderboard = async () => {
      try {
        const entries = await getLeaderboardEntries(50);
        
        // Convert LeaderboardEntry[] to User[] for UI compatibility
        const converted = entries
          .map(entryToUser)
          .slice(0, 20); // Limit to top 20

        setLeaders(converted);
        setLoading(false);
        setError(null);
      } catch (processingError: any) {
        if (import.meta.env.DEV) {
          console.error('[LEADERBOARD] Error loading leaderboard entries:', processingError);
        }
        setLeaders([]);
        setLoading(false);
        setError('UNKNOWN_ERROR');
      }
    };

    loadLeaderboard();
  }, [authReady, activeTab, user?.id]);

  // Friends tab: Real-time subscription to following users
  React.useEffect(() => {
    // 🔑 CRITICAL: Block ALL snapshots until auth is ready
    if (!authReady) return;
    
    if (activeTab !== 'friends') return;

    setLoading(true);
    setError(null);

    // Subscribe to real-time following list changes
    const unsubscribe = subscribeToFollowing(user.id, async (followingIds) => {
      try {
        // Include current user in friends tab
        const userIdsToLoad = followingIds.length > 0 
          ? [user.id, ...followingIds]
          : [user.id];

        // Load public profiles for following users (including current user)
        const profiles = await getPublicProfilesByIds(userIdsToLoad);
        
        // Convert PublicProfile[] to User[] and add current user if not in results
        let converted = profiles.map(profileToUser);

        // Ensure current user is included (may not be in publicProfiles if private)
        const hasCurrentUser = converted.some(u => u.id === user.id);
        if (!hasCurrentUser) {
          // Add current user from props (they may have private profile)
          converted.push({
            id: user.id,
            userId: user.id,
            name: user.name || '',
            avatar: user.avatar || user.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`,
            level: user.level || 1,
            xp: user.xp || 0,
            title: user.title,
            email: user.email || '',
            badges: user.badges || [],
            public: user.public !== false,
            role: user.role || 'user',
            plan: user.plan || 'free',
            activeChallenges: user.activeChallenges || [],
          } as User);
        }

        // Sort: xp DESC, level DESC, name ASC (same as global tab)
        converted.sort((a: User, b: User) => {
          const xpDiff = (b.xp || 0) - (a.xp || 0);
          if (xpDiff !== 0) return xpDiff;

          const lvlDiff = (b.level || 1) - (a.level || 1);
          if (lvlDiff !== 0) return lvlDiff;

          return String(a.name || '').localeCompare(String(b.name || ''));
        });

        setLeaders(converted.slice(0, 20));
        setLoading(false);
        setError(null);
        
        if (import.meta.env.DEV) {
          console.log('[LEADERBOARD] Friends list updated (real-time):', {
            userId: user.id,
            followingCount: followingIds.length,
            leadersCount: converted.length
          });
        }
      } catch (processingError: any) {
        if (import.meta.env.DEV) {
          console.error('[LEADERBOARD] Error processing friends profiles:', processingError);
        }
        setLeaders([]);
        setLoading(false);
        setError('UNKNOWN_ERROR');
      }
    });

    return () => {
      try {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      } catch {
        // Silently ignore cleanup errors
      }
    };
  }, [activeTab, user?.id]);

  // Timeout to prevent infinite loading
  React.useEffect(() => {
    if (loading && leaders.length === 0) {
      const timeout = setTimeout(() => {
        setLoading(false);
      }, 3000); // Max 3 seconds loading
      return () => clearTimeout(timeout);
    }
  }, [loading, leaders.length]);

  // ---- UI STATES ----
  // Show placeholder instead of infinite loading
  if (loading && leaders.length === 0) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] overflow-hidden p-8">
        <div className="flex items-center justify-center gap-3 text-slate-400">
          <div className="w-5 h-5 rounded-full border-2 border-slate-700 border-t-slate-300 animate-spin" />
          <span className="text-xs font-black uppercase tracking-widest">
            {t ? t('dashboard.loading') : 'Loading'}
          </span>
        </div>
      </div>
    );
  }

  // Show meaningful placeholder when empty (not an error state)
  if (!loading && leaders.length === 0 && !error) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] overflow-hidden p-8">
        <div className="text-center space-y-4 py-8">
          <TrendingUp className="text-slate-600 mx-auto" size={48} />
          <h3 className="text-lg font-black text-slate-400">
            Top Performers
          </h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Top performers will appear once the community grows. Be the first to make your mark!
          </p>
        </div>
      </div>
    );
  }

  if (error === 'ACCESS_DENIED') {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] overflow-hidden p-8">
        <div className="text-center space-y-4">
          <Lock className="text-orange-400 mx-auto" size={48} />
          <h3 className="text-lg font-black text-white">
            Leaderboard Unavailable
          </h3>
          <p className="text-sm text-slate-400">
            We're having trouble loading the leaderboard right now. Check back in a moment.
          </p>
        </div>
      </div>
    );
  }

  if (error === 'UNKNOWN_ERROR') {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] overflow-hidden p-8">
        <div className="text-center space-y-4">
          <AlertCircle className="text-red-400 mx-auto" size={48} />
          <h3 className="text-lg font-black text-white">
            Something Went Wrong
          </h3>
          <p className="text-sm text-slate-400">
            We couldn't load the leaderboard. Please refresh the page or try again later.
          </p>
        </div>
      </div>
    );
  }

  // ---- MAIN UI ----
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] overflow-hidden">
      <div className="p-6 border-b border-slate-800 bg-slate-900/50 flex justify-between items-center">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('global')}
            className={`text-sm font-black transition-all ${
              activeTab === 'global' ? 'text-emerald-400' : 'text-slate-500'
            }`}
          >
            {t ? t('dashboard.leaderboard_global') : 'Global'}
          </button>
          <button
            onClick={() => setActiveTab('friends')}
            className={`text-sm font-black transition-all ${
              activeTab === 'friends' ? 'text-emerald-400' : 'text-slate-500'
            }`}
          >
            {t ? t('dashboard.leaderboard_friends') : 'Friends'}
          </button>
        </div>
        <Filter size={18} className="text-slate-500" />
      </div>

      <div className="p-2 space-y-1">
        {leaders.length === 0 ? (
          <div className="p-8 text-center space-y-3">
            <TrendingUp className="text-slate-600 mx-auto" size={32} />
            <p className="text-slate-500 text-sm font-bold">
              No users to display yet
            </p>
            <p className="text-xs text-slate-600">
              The community is growing. Check back soon!
            </p>
          </div>
        ) : (
          leaders.map((leader, index) => (
            <div
              key={leader.id}
              className={`flex items-center gap-4 p-4 rounded-2xl transition-all ${
                leader.id === user.id
                  ? 'bg-emerald-500/10 border border-emerald-500/20'
                  : 'hover:bg-slate-800/30'
              }`}
            >
              <span
                className={`w-8 font-black text-lg text-center ${
                  index === 0
                    ? 'text-yellow-500'
                    : index === 1
                      ? 'text-slate-300'
                      : index === 2
                        ? 'text-orange-400'
                        : 'text-slate-600'
                }`}
              >
                {index + 1}
              </span>

              <img
                src={(leader as any).avatar}
                className="w-10 h-10 rounded-full border border-slate-700 object-cover"
                alt={leader.name}
              />

              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate flex items-center gap-2">
                  {leader.name}
                  {leader.level >= 30 && (
                    <Shield size={12} className="text-yellow-500" fill="currentColor" />
                  )}
                </p>
                <p className="text-[10px] text-slate-500 uppercase font-black">
                  {(t ? t('profile.level_short') : 'Lv')} {leader.level} • {(leader as any).title || (t ? t('dashboard.athlete') : 'Athlete')}
                </p>
              </div>

              <div className="text-right">
                <span className="font-black text-emerald-400 text-sm">
                  {((leader as any).xp || 0).toLocaleString()}
                </span>
                <p className="text-[8px] text-slate-600 font-bold uppercase tracking-tighter">
                  {t ? t('dashboard.xp_total') : 'XP Total'}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
